import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface OrderEvent {
  type: 'new_order' | 'order_updated' | 'heartbeat' | 'connected' | 'error';
  order?: any;
  message?: string;
  timestamp?: number;
}

interface UseOrderEventsReturn {
  isConnected: boolean;
  newOrdersCount: number;
  markOrdersAsSeen: () => void;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

/**
 * Hook profesional para actualizaciones en tiempo real de pedidos
 * Implementa Server-Sent Events con fallback a polling
 * Maneja reconexión automática y estados de error
 */
export function useOrderEvents(): UseOrderEventsReturn {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [isConnected, setIsConnected] = useState(false);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [lastOrderId, setLastOrderId] = useState<number>(0);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  
  // Set para rastrear pedidos ya procesados (evitar duplicados)
  const processedOrdersRef = useRef<Set<number>>(new Set());

  // 🐛 DEBUG: Log del contador
  useEffect(() => {
    console.log('🔢 Contador de pedidos nuevos:', newOrdersCount);
  }, [newOrdersCount]);

  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 5000; // 5 segundos
  const POLLING_INTERVAL = 5000; // 5 segundos como fallback

  // Función para invalidar y refrescar queries
  const refreshOrders = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    queryClient.refetchQueries({ queryKey: ['pedidos'] });
  }, [queryClient]);

  // Función para manejar nuevos pedidos
  const handleNewOrder = useCallback((order: any) => {
    console.log('🆕 Nueva orden recibida:', order);
    
    // 🛡️ DEDUPLICACIÓN: Verificar si ya procesamos este pedido
    if (processedOrdersRef.current.has(order.id)) {
      console.log(`⏭️ Pedido #${order.id} ya fue procesado, ignorando duplicado`);
      return;
    }
    
    // Marcar como procesado
    processedOrdersRef.current.add(order.id);
    console.log(`✅ Pedido #${order.id} marcado como procesado`);
    
    // Mostrar notificación
    toast.success(
      `¡Nueva orden #${order.id}!`,
      {
        description: `${order.customer_name} - $${order.total}`,
        duration: 5000,
        action: {
          label: 'Ver',
          onClick: () => window.open(`/panel/pedidos/${order.id}`, '_blank')
        }
      }
    );

    // Reproducir sonido de notificación
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Fallback: usar Web Audio API si está disponible
        if ('AudioContext' in window || 'webkitAudioContext' in window) {
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = 800;
          gainNode.gain.value = 0.1;
          
          oscillator.start();
          oscillator.stop(audioContext.currentTime + 0.2);
        }
      });
    } catch (error) {
      console.warn('No se pudo reproducir sonido de notificación:', error);
    }

    // Incrementar contador SOLO si requiere acción (excluir finalizados)
    const estadosFinalizados = [
      'delivered_confirmed',
      'cancelled',
      'refunded',
      'return_completed_refunded',
      'return_completed_replaced',
      'return_rejected'
    ];
    
    if (!estadosFinalizados.includes(order.status)) {
      console.log(`➕ Incrementando contador (estado: ${order.status})`);
      setNewOrdersCount(prev => prev + 1);
    } else {
      console.log(`⏭️ Pedido finalizado, NO incrementar contador (estado: ${order.status})`);
    }
    setLastOrderId(order.id);

    // Refrescar datos
    refreshOrders();
  }, [refreshOrders]);

  // Función para establecer conexión WebSocket (método principal)
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('tiendaya_token'); // FIX: Usar el nombre correcto del token
    if (!token) {
      console.warn('No hay token de autenticación disponible');
      setConnectionStatus('error');
      return;
    }

    setConnectionStatus('connecting');
    
    try {
      // Cerrar conexión existente si existe
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Crear conexión WebSocket (no SSE)
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // FIX: Usar el host del backend (api.tiendasya.store) en lugar del frontend
      const backendHost = import.meta.env.VITE_API_URL?.replace(/^https?:\/\//, '').replace(/\/$/, '') || 'api.tiendasya.store';
      const wsUrl = `${wsProtocol}//${backendHost}/ws/orders/?token=${token}`;
      
      console.log('🔌 Conectando WebSocket de pedidos...');
      
      const ws = new WebSocket(wsUrl);
      eventSourceRef.current = ws as any; // Hack para reutilizar la ref

      ws.onopen = () => {
        console.log('✅ WebSocket conectado');
        setIsConnected(true);
        setConnectionStatus('connected');
        setReconnectAttempts(0);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 WebSocket mensaje recibido:', data);
          
          switch (data.type) {
            case 'connected':
              console.log('🔗 WebSocket conectado:', data.message);
              break;
              
            case 'new_order':
              if (data.order) {
                console.log('🆕 NUEVO PEDIDO RECIBIDO VIA WEBSOCKET:', data.order);
                handleNewOrder(data.order);
              }
              break;
              
            case 'order_updated':
              if (data.order) {
                console.log('🔄 PEDIDO ACTUALIZADO VIA WEBSOCKET:', data.order);
                // Emitir evento personalizado para que usePedido lo escuche
                const updateEvent = new CustomEvent('websocket-order-updated', {
                  detail: { order: data.order }
                });
                window.dispatchEvent(updateEvent);
                
                // También refrescar la lista de pedidos
                refreshOrders();
              }
              break;
              
            case 'heartbeat':
              // Mantener conexión viva
              break;
              
            default:
              console.log('📨 Evento WebSocket desconocido:', data);
          }
        } catch (error) {
          console.error('Error procesando mensaje WebSocket:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ Error en WebSocket:', error);
        setIsConnected(false);
        setConnectionStatus('error');
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket cerrado:', event.code, event.reason);
        setIsConnected(false);
        setConnectionStatus('disconnected');
        
        // Intentar reconexión automática
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          setReconnectAttempts(prev => prev + 1);
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`🔄 Reintentando WebSocket (${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);
            connectWebSocket();
          }, RECONNECT_DELAY);
        } else {
          console.warn('🚫 Máximo de reintentos WebSocket alcanzado, activando polling de respaldo');
          startPollingFallback();
        }
      };

    } catch (error) {
      console.error('Error creando WebSocket:', error);
      setConnectionStatus('error');
      startPollingFallback();
    }
  }, [handleNewOrder, reconnectAttempts]);

  // Función de polling como fallback
  const startPollingFallback = useCallback(() => {
    console.log('🔄 Iniciando polling de respaldo');
    setConnectionStatus('connected'); // Mostrar como conectado aunque sea polling
    
    const token = localStorage.getItem('tiendaya_token'); // FIX: Usar el nombre correcto del token
    if (!token) return;

    const pollOrders = async () => {
      try {
        const response = await fetch('/api/orders?page_size=10', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const orders = data.results || [];
          
          if (orders.length > 0) {
            const latestOrderId = Math.max(...orders.map((o: any) => o.id));
            
            if (lastOrderId > 0 && latestOrderId > lastOrderId) {
              const newOrders = orders.filter((o: any) => o.id > lastOrderId);
              newOrders.forEach(handleNewOrder);
            }
            
            if (lastOrderId === 0) {
              setLastOrderId(latestOrderId);
            }
          }
        }
      } catch (error) {
        console.error('Error en polling de respaldo:', error);
      }
    };

    // Ejecutar inmediatamente y luego cada intervalo
    pollOrders();
    pollingIntervalRef.current = setInterval(pollOrders, POLLING_INTERVAL);
  }, [lastOrderId, handleNewOrder]);

  // Función para limpiar conexiones
  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      if (eventSourceRef.current instanceof WebSocket) {
        eventSourceRef.current.close();
      } else {
        eventSourceRef.current.close();
      }
      eventSourceRef.current = null;
    }
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setIsConnected(false);
    setConnectionStatus('disconnected');
  }, []);

  // Función para marcar pedidos como vistos
  const markOrdersAsSeen = useCallback(() => {
    console.log('✅ Marcando pedidos como vistos, reseteando contador a 0');
    console.log(`📊 Contador actual antes de resetear: ${newOrdersCount}`);
    setNewOrdersCount(0);
    // Limpiar el Set de pedidos procesados para permitir nuevas notificaciones
    processedOrdersRef.current.clear();
    console.log('🧹 Set de pedidos procesados limpiado');
  }, [newOrdersCount]); // Agregar newOrdersCount como dependencia para el log

  // Efecto principal
  useEffect(() => {
    // Conectar WebSocket (método principal - PUSH notifications)
    connectWebSocket();

    // Cleanup al desmontar
    return cleanup;
  }, [connectWebSocket, cleanup]);

  // Efecto para manejar visibilidad de la página
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected) {
        console.log('🔄 Página visible, reintentando conexión WebSocket');
        connectWebSocket();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isConnected, connectWebSocket]);

  return {
    isConnected,
    newOrdersCount,
    markOrdersAsSeen,
    connectionStatus
  };
}